import { useQuery } from "@apollo/client";
import { Button } from "@samdesk/components/Button";
import {
	createColumnHelper,
	DataTablePrototype,
} from "@samdesk/components/DataTablePrototype";
import { showPrompt } from "@samdesk/components/Prompt";
import { Stack } from "@samdesk/components/Stack";
import { Text } from "@samdesk/components/Text";
import { Tooltip } from "@samdesk/components/Tooltip";
import { useMemo, useState } from "react";
import Breadcrumbs from "routes/settings/Breadcrumbs";
import CenteredCircularProgress from "ui-library/CenteredCircularProgress";
import SettingsPageTitle from "ui-library/SettingsPageTitle";
import useSnackbar from "utils/useSnackbar";
import { GenericIntegrationModal } from "./GenericIntegrationModal";
import {
	Integration,
	IntegrationResult,
	ListIntegrationsResult,
} from "./Integration";

interface GenericListPageProps {
	integration: Integration;
}

const columnHelper = createColumnHelper<IntegrationResult>();
const getColumns = (integration: Integration) => {
	const statusCell = columnHelper.accessor("disabled", {
		id: "status",
		header: "Status",
		maxSize: 60,
		cell: ({ row }) => {
			if ("disabled" in row.original) {
				return <Text>{row.original.disabled ? "Disabled" : "Enabled"}</Text>;
			} else {
				return <></>;
			}
		},
	});
	const actionsCell = columnHelper.display({
		id: "actions",
		maxSize: 100,
		cell: ({ row }) => (
			<Actions
				gid={row.original.gid}
				integration={integration}
				name={row.original.name}
			/>
		),
	});

	return [
		...integration.listFields.map((fieldName) => {
			const field = integration.fields.find(
				(field) => field.name === fieldName
			);

			if (!field) {
				throw new Error(`Column definition not found for ${fieldName}`);
			}

			return columnHelper.accessor(field.name, {
				id: field.name,
				header: field.label,
				enableSorting: false,
				cell: ({ row }) => {
					return <Text>{row.original?.[field.name]}</Text>;
				},
			});
		}),
		statusCell,
		actionsCell,
	];
};

function isAtAccountLimit(data: ListIntegrationsResult | undefined) {
	const integrationLimit =
		data?.account?.settings?.limit ?? Number.POSITIVE_INFINITY;

	return (data?.integrations?.length ?? 0) >= integrationLimit;
}

export function GenericListPage({ integration }: GenericListPageProps) {
	const [createModalOpen, setCreateModalOpen] = useState(false);

	const { data, loading } = useQuery(integration.actions.list, {
		variables: {},
	});

	const atAcccountLimit = useMemo(() => {
		return isAtAccountLimit(data);
	}, [data]);

	const columns = useMemo(() => getColumns(integration), [integration]);

	return (
		<>
			<Breadcrumbs />
			<SettingsPageTitle>
				{integration.name + " Integrations"}
			</SettingsPageTitle>
			<Stack spacing="larger">
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Text>{integration.listPageDescription}</Text>
					<Tooltip
						text="Please delete an integration to create a new one."
						disabled={!atAcccountLimit}
					>
						<Button
							label="Create Integration"
							prominence="primary"
							onClick={() => {
								setCreateModalOpen(true);
							}}
							disabled={atAcccountLimit}
						/>
					</Tooltip>
				</Stack>
				{loading ?
					<CenteredCircularProgress />
				:	<DataTablePrototype
						columns={columns}
						getRowId={(row: IntegrationResult) => row.gid}
						data={data?.integrations ?? []}
						showGlobalFilter={false}
					/>
				}

				<GenericIntegrationModal
					integration={integration}
					open={createModalOpen}
					onClose={() => setCreateModalOpen(false)}
				/>
			</Stack>
		</>
	);
}

interface ActionsProps {
	gid: GID;
	integration: Integration;
	name: string | null;
}

function Actions({ gid, integration, name }: ActionsProps) {
	const { handleError, handleSuccess } = useSnackbar();
	const [modalOpen, setModalOpen] = useState(false);

	return (
		<>
			<Stack direction="row" spacing="small" justifyContent="center">
				<Button
					label="Edit"
					prominence="secondary"
					onClick={() => {
						setModalOpen(true);
					}}
				/>
				<Button
					label="Delete"
					prominence="secondary"
					intent="destruction"
					onClick={async () => {
						const { state } = await showPrompt({
							title: "Confirm Delete",
							description: `Are you sure you want to delete "${name}"?`,
							promptType: "confirm",
						});

						if (state === "ok") {
							const result = await integration.actions.delete(gid);

							if (result.error) {
								handleError(result.error.message);
							} else if (result.success) {
								handleSuccess(`Deleted ${name}`);
							}
						}
					}}
				/>
			</Stack>
			<GenericIntegrationModal
				integrationGID={gid}
				integration={integration}
				open={modalOpen}
				onClose={() => setModalOpen(false)}
			/>
		</>
	);
}
